import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getSharedChildren,
  getSharedNode,
  getSharedPlan,
  negotiateSharedRealtime,
} from './api/client';
import { describeError } from './api/errors';
import type {
  ChildListingResponse,
  PlanResponse,
  RealtimeConnectionResponse,
  SharedPlanResponse,
} from './api/types';
import { useMessages } from './i18n/context';
import { defaultDeps as defaultSubscriptionDeps, subscribe } from './realtime/subscription';
import { useRealtime } from './realtime/useRealtime';
import { ReadOnlyTree } from './ReadOnlyTree';

/** The share-doorway calls the viewer makes and the realtime controller — injectable in tests. */
export interface SharedViewerDeps {
  getSharedPlan: (shareId: string) => Promise<SharedPlanResponse>;
  getSharedNode: (shareId: string, nodeId: string) => Promise<SharedPlanResponse>;
  getSharedChildren: (shareId: string, nodeId?: string) => Promise<ChildListingResponse[]>;
  negotiateSharedRealtime: (
    shareId: string,
    nodeId?: string,
  ) => Promise<RealtimeConnectionResponse>;
  subscribe: typeof subscribe;
}

export const defaultSharedViewerDeps: SharedViewerDeps = {
  getSharedPlan,
  getSharedNode,
  getSharedChildren,
  negotiateSharedRealtime,
  subscribe,
};

interface View {
  /** The share link and descendant this view was loaded for (null = the linked resource). */
  shareId: string;
  nodeId: string | null;
  shared: SharedPlanResponse;
  children: readonly ChildListingResponse[];
}

/**
 * The anonymous read-only view behind a share link (ADR-027 §6): the linked resource's full
 * content and its public sub-planlets, navigable down the subtree (every hop chain-checked
 * server-side) and back up to the linked root. Every failure is the server's uniform 404.
 * Live like the editor (ADR-028 §6): while a plan is showing, one receive-only subscription —
 * negotiated through the share doorway for the very node on screen — refetches its content
 * when a collaborator's operation moves the version ahead of ours, and retitles it on a
 * collaborator's rename.
 */
export function SharedViewer({
  shareId,
  deps = defaultSharedViewerDeps,
}: {
  shareId: string;
  deps?: SharedViewerDeps;
}) {
  const t = useMessages();
  const g = t.grove;
  /** The descendant being viewed, or null for the linked resource itself. */
  const [nodeId, setNodeId] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);

  // The editor's controller, bound to the share doorway for the link and node ON SCREEN — the
  // view's own, not the props or the navigation state, and the deps through a ref — so the
  // binding can only change in a render where the shown plan changes too: the hook stops one
  // subscription and opens the next, never re-opens a live one. Negotiate for that link and
  // node, refetch the content the way the view loaded it.
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const shownShareId = view === null ? shareId : view.shareId;
  const shownNode = view === null ? null : view.nodeId;
  const subscribeShared = useMemo<typeof subscribe>(
    () => (planId, currentVersion, handlers) => {
      const d = depsRef.current;
      return d.subscribe(planId, currentVersion, handlers, {
        ...defaultSubscriptionDeps,
        negotiate: () => d.negotiateSharedRealtime(shownShareId, shownNode ?? undefined),
        fetchPlan: () =>
          (shownNode === null
            ? d.getSharedPlan(shownShareId)
            : d.getSharedNode(shownShareId, shownNode)
          ).then((shared) => shared.plan),
      });
    },
    [shownShareId, shownNode],
  );

  // Adopt a refetched document only for the plan on screen and only if it is still newer.
  const refreshed = (plan: PlanResponse) =>
    setView((current) =>
      current !== null &&
      plan.id === current.shared.plan.id &&
      plan.version > current.shared.plan.version
        ? { ...current, shared: { ...current.shared, plan } }
        : current,
    );

  // A collaborator's rename (ADR-028 §3 as amended 2026-09-16): retitle the plan on screen from
  // the name the notice carried — never a re-read through the doorway, which resolves the name
  // through the backend's node cache and could answer the old one.
  const renamed = (planId: string, name: string) =>
    setView((current) =>
      current !== null && current.shared.plan.id === planId && current.shared.name !== name
        ? { ...current, shared: { ...current.shared, name } }
        : current,
    );

  useRealtime(
    view?.shared.plan.id ?? null,
    view?.shared.plan.version ?? null,
    { onPlan: refreshed, onRenamed: renamed },
    subscribeShared,
  );

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setError(null);
    const content =
      nodeId === null ? deps.getSharedPlan(shareId) : deps.getSharedNode(shareId, nodeId);
    Promise.all([content, deps.getSharedChildren(shareId, nodeId ?? undefined)])
      .then(([shared, children]) => {
        if (!cancelled) setView({ shareId, nodeId, shared, children });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setError({ cause: failure });
      });
    return () => {
      cancelled = true;
    };
  }, [shareId, nodeId, deps]);

  return (
    <section className="shared-viewer">
      <nav className="shared-navigation" aria-label={g.sharedNavigation}>
        <a href="/">{t.app.title}</a>
        <span className="access-badge">{t.sharedViewer.sharedView} · {t.role.reader}</span>
        {nodeId !== null && (
          <button className="button-quiet" onClick={() => setNodeId(null)}>
            {t.sharedViewer.backToRoot}
          </button>
        )}
      </nav>
      {error !== null && (
        <p className="notice notice-error" role="alert">{describeError(error.cause, t.errors)}</p>
      )}
      {view === null && error === null && <p className="empty-state">{t.sharedViewer.loading}</p>}
      {view !== null && (
        <>
          <div className="page-heading">
            <div>
              <p className="eyebrow">{g.sharedPlan}</p>
              <h2>{view.shared.name}</h2>
            </div>
            <span className="version-badge" aria-label={t.sharedViewer.planVersion}>
              v{view.shared.plan.version}
            </span>
          </div>
          <div className="card shared-content">
            {view.shared.plan.children.length === 0 ? (
              <p className="empty-state">{t.sharedViewer.empty}</p>
            ) : (
              <ReadOnlyTree components={view.shared.plan.children} />
            )}
          </div>
          <h3>{t.sharedViewer.subPlanlets}</h3>
          {view.children.length === 0 ? (
            <p className="empty-state">{t.sharedViewer.noPublicSubPlanlets}</p>
          ) : (
            <ul className="subplan-grid">
              {view.children.map((child) => (
                <li className="subplan-card" key={child.id}>
                  <button className="subplan-title" onClick={() => setNodeId(child.id)}>
                    {child.name}
                    <span className="plan-card-arrow" aria-hidden="true">
                      →
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
